import React, { useEffect, useState, Suspense } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import MainLayout from "./layouts/MainLayout";
import HomePage from "./pages/Home/HomePage";
import PhotoArtPage from "./pages/PhotoArt/PhotoArtOverviewPage";
import ContactPage from "./pages/Contact/ContactPage";
import SubpageLayout from "./layouts/SubpageLayout";

import ProductsPage from "./pages/Products/ProductsPage";
import PhotoFamilyTreesOverviewPage from "./pages/PhotoFamilyTrees/PhotoFamilyTreesOverviewPage";
import InDevelopmentPage from "./pages/InDevelopment/InDevelopmentPage";
import CustomLayoutOptionsPage from "./pages/PhotoFamilyTrees/CustomLayoutOptionsPage";
import ExampleGalleryPage from "./pages/PhotoFamilyTrees/ExampleGalleryPage";
import PhotoFamilyTreesPage from "./pages/shop/PhotoFamilyTreesPage";
import ProductPage from "./pages/shop/ProductPage";
import EcommercePage from "./pages/shop/EcommercePage";
import MissionaryPhotoArtPage from "./pages/shop/MissionaryPhotoArtPage";
import GenerationalArtPage from "./pages/shop/GenerationalArtPage";
import GiftCertificatePage from "./pages/shop/GiftCertificatesPage";
import PhotographGuidelinesPage from "./pages/Guidelines/PhotographGuidelinesPage";
import ScanningGuidelines from "./pages/Guidelines/ScanningGuidelinesPage";
import CustomPhotoFamilyTreeForm from "./pages/Forms/CustomPhotoFamilyTreeForm";
import GenerationalForm from "./pages/Forms/GenerationalForm";
import FamilyNamesForm from "./pages/Forms/FamilyNamesForm";
import MissionaryForm from "./pages/Forms/MissionaryForm";
import PhotoEnhancementsPage from "./pages/PhotoFamilyTrees/PhotoEnhancementsPage";
import BackgroudColorsPage from "./pages/PhotoFamilyTrees/BackgroundColorsPage";
import LdsTempleBackgroundsPage from "./pages/PhotoFamilyTrees/LdsTempleBackgroundPage";
import GenerationalArtOverviewPage from "./pages/GenerationalArt/GenerationalArtOverviewPage";
import PoliciesPage from "./pages/PoliciesPage";
import TermsOfServicePage from "./pages/Legal/TermsOfServicePage";
import PrivacyPolicyPage from "./pages/Legal/PrivacyPolicyPage";
import PriceListPage from "./pages/PriceListPage";
import SignIn from "./pages/Account/SignIn";
import SignUpPage from "./pages/Account/SignUp";
import ResetPassword from "./pages/Account/ResetPassword";
import AccountPage from "./pages/Account/AccountPage";
import CheckoutPage from "./pages/Checkout/CheckoutPage";
import PaymentConfirmationPage from "./pages/Checkout/PaymentConfirmationPage";
import PaymentSuccess from "./pages/Checkout/PaymentSuccess";
import ShoppingCartPage from "./pages/Checkout/ShoppingCartPage";
import AdminDashboard from "./pages/Admin/AdminDashboard";
import AdminRoute from "./components/AdminRoute";
import { FormTypes } from "./pages/shop/productTypes";
import { useAuth } from "./firebase";
import { doc, getDoc, getFirestore } from "firebase/firestore";

import { CircularProgress, Box } from "@mui/material";
import { APP_CONFIG } from "./config/appConfig";
import { getLocalStorage } from "./utils/storage";
import PendingApprovalPage from "./pages/Account/PendingApprovalPage";
import StudioDevPreview from "./dev/StudioDevPreview";

// Lazy so the FamilySearch SDK chain (firebase/functions) only loads when the
// feature is enabled and the callback route is actually visited.
const FamilySearchCallback = React.lazy(
  () => import("./pages/Account/FamilySearchCallback")
);



// Loading component for lazy loaded routes
const LoadingFallback = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "200px",
    }}
  >
    <CircularProgress />
  </Box>
);

const mainLayoutRoutes = [
  {
    path: "/",
    component: <HomePage />,
  },


  {
    path: "/sign-in",
    component: <SignIn />,
  },
  {
    path: "/sign-up",
    component: <SignUpPage />,
  },
  {
    path: "/reset-password",
    component: <ResetPassword />,
  },
  {
    path: "/account/*",
    component: <AccountPage />,
  },
  {
    path: "/admin",
    component: (
      <AdminRoute>
        <AdminDashboard />
      </AdminRoute>
    ),
  },
];

const subPageLayoutRoutes = [
  {
    path: "/contact-us",
    component: <ContactPage />,
  },
  {
    path: "/missionary-photo-art",
    component: <PhotoArtPage />,
  },

  {
    path: "/purchase",
    component: <ProductsPage />,
  },
  {
    path: "/photo-family-trees",
    component: <PhotoFamilyTreesOverviewPage />,
  },
  {
    path: "/404",
    component: <InDevelopmentPage />,
  },
  {
    path: "/custom-layout-options",
    component: <CustomLayoutOptionsPage />,
  },
  {
    path: "/family-tree-example-gallery",
    component: <ExampleGalleryPage />,
  },
  {
    path: "/shop/Photo-Family-Trees",
    component: <PhotoFamilyTreesPage />,
  },
  // POST-LAUNCH: Re-enable Missionary Photo Art route
  // {
  //   path: "/shop/Missionary-Photo-Art",
  //   component: <MissionaryPhotoArtPage />,
  // },
  {
    path: "/shop/Generational-Art",
    component: <GenerationalArtPage />,
  },
  {
    path: "/shop/Gift-Certificates",
    component: <GiftCertificatePage />,
  },
  {
    path: "/shop/:categoryId/:productId",
    component: <EcommercePage />,
  },
  {
    path: "/photograph-guidelines",
    component: <PhotographGuidelinesPage />,
  },
  {
    path: "/scanning-guidelines",
    component: <ScanningGuidelines />,
  },
  {
    path: "/photo-enhancements",
    component: <PhotoEnhancementsPage />,
  },
  {
    path: "/background-colors-for-family-trees",
    component: <BackgroudColorsPage />,
  },
  {
    path: "/family-trees-with-lds-temple-backgrounds",
    component: <LdsTempleBackgroundsPage />,
  },
  {
    path: "/generational-art",
    component: <GenerationalArtOverviewPage />,
  },
  {
    path: "/policies",
    component: <PoliciesPage />,
  },
  {
    path: "/terms-of-service",
    component: <TermsOfServicePage />,
  },
  {
    path: "/privacy-policy",
    component: <PrivacyPolicyPage />,
  },
  {
    path: "/price-list",
    component: <PriceListPage />,
  },


  {
    path: "/checkout",
    component: <CheckoutPage />,
  },
  {
    path: "/payment-confirmation",
    component: <PaymentConfirmationPage />,
  },
  {
    path: "/payment-success",
    component: <PaymentSuccess />,
  },
  {
    path: "/cart",
    component: <ShoppingCartPage />,
  },
];

const formRoutes = [
  {
    path: "/forms/customizing-your-family-tree",
    component: <CustomPhotoFamilyTreeForm />,
  },
  {
    path: "/forms/generational-names",
    component: <GenerationalForm />,
  },
  {
    path: "/forms/family-names-and-birthdates",
    component: <FamilyNamesForm />,
  },
  {
    path: "/forms/missionary-form",
    component: <MissionaryForm />,
  },
];

export const getFormPath = (formType: FormTypes): string => {
  switch (formType) {
    case "Missionary":
      return "/forms/missionary-form";
    case "Generational":
      return "/forms/generational-names";
    case "FamilyNames":
      return "/forms/family-names-and-birthdates";
  }
};

// const routesToDo = [
//   "/shop/Misc/PURCHASES-made-through-RETAIL-STORES",
// ];

const mainRoutesObjects = mainLayoutRoutes.map((route) => ({
  path: route.path,
  component: route.component,
  layout: MainLayout,
}));

const subRoutesObjects = subPageLayoutRoutes.map((route) => ({
  path: route.path,
  component: route.component,
  layout: SubpageLayout,
}));

// const formRouteObjects = formRoutes.map((route) => ({
//   path: route.path,
//   component: route.component,
//   layout: SubpageLayout,
// }));

// const inDevelopmentObjects = routesToDo.map((route) => ({
//   path: route,
//   component: <InDevelopmentPage />,
//   layout: SubpageLayout,
// }));

const allRoutes = [
  ...mainRoutesObjects,
  ...subRoutesObjects,
  // ...formRouteObjects,
  // ...inDevelopmentObjects,
];

const AppRouter = () => {
  const authContext = useAuth();
  const [isWhitelisted, setIsWhitelisted] = useState<boolean | null>(null);
  const db = getFirestore();

  useEffect(() => {
    const checkWhitelist = async () => {
      if (!APP_CONFIG.REQUIRE_WHITELIST) {
        // Skip whitelist check if disabled
        setIsWhitelisted(true);
        return;
      }

      if (authContext?.user?.email) {
        const docRef = doc(db, "whitelist", authContext.user.email);
        const docSnap = await getDoc(docRef);
        setIsWhitelisted(docSnap.exists());
      } else {
        setIsWhitelisted(false);
      }
    };
    checkWhitelist();
  }, [authContext, db]);

  // Check if maintenance mode is enabled
  const bypassMaintenance = getLocalStorage("allow_access") === "true";

  if (APP_CONFIG.MAINTENANCE_MODE && !bypassMaintenance) {
    return <InDevelopmentPage />;
  }

  if (!authContext) {
    return <div>Loading...</div>;
  }

  const { user } = authContext;

  if (!user) {
    return (
      <Router>
        <Routes>
          <Route path="/sign-in" element={<SignIn />} />
          <Route path="/sign-up" element={<SignUpPage />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* Legal pages must be publicly reachable (payment processor requirement) */}
          <Route
            path="/terms-of-service"
            element={<SubpageLayout><TermsOfServicePage /></SubpageLayout>}
          />
          <Route
            path="/privacy-policy"
            element={<SubpageLayout><PrivacyPolicyPage /></SubpageLayout>}
          />
          {/* Allow dev preview even if not logged in (optional, but easier) */}
          {APP_CONFIG.DEVELOPMENT_MODE && (
             <Route path="/dev/studio-preview" element={<StudioDevPreview />} />
          )}
          <Route path="*" element={<Navigate to="/sign-in" />} />
        </Routes>
      </Router>
    );
  }

  // Allow dev preview for logged in but unapproved users
  if (window.location.pathname.includes("/dev/studio-preview")) {
      return (
        <Router>
            <Routes>
                <Route path="/dev/studio-preview" element={<StudioDevPreview />} />
            </Routes>
        </Router>
      );
  }

  if (isWhitelisted === null) {
    return <div>Loading...</div>; // Or a loading spinner
  }

  if (!isWhitelisted && !bypassMaintenance) {
    return <PendingApprovalPage />;
  }

  return (
    <Router>
      <Routes>
        {APP_CONFIG.FEATURES.FAMILYSEARCH && (
          <Route
            path="/auth/familysearch/callback"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <FamilySearchCallback />
              </Suspense>
            }
          />
        )}
        {allRoutes.map((route, index) => {
          const Layout = route.layout || React.Fragment;
          return (
            <Route
              key={index}
              path={route.path}
              element={<Layout>{route.component}</Layout>}
            />
          );
        })}
      </Routes>
    </Router>
  );
};

export default AppRouter;
